import type { TestContext } from "yup";
import * as yup from "yup";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";
import {
    FormatHelper,
    StringHelper,
} from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { ImageFileDto } from "../../../assets/utils/lib/imageFileDto";
import { BannerDto, LookupDto } from "../../../services/dto";
import { useResolvedBannerCommunityId } from "./bannerCommunity";
import { BannerService } from "./bannerService";

interface BannerFormProps {
    data?: BannerDto;
    show: boolean;
    bannerTypes?: LookupDto[];
    bannerTypesLoading?: boolean;
    onHide: () => void;
    onSubmit: (values: BannerDto) => DrawerFormSaveResult;
    communityId?: string;
    totalRecords?: number;
}

type ImageInput = ImageFileDto | File | string;

const hasExtension = (x: unknown): x is { extension?: string | null } =>
    typeof (x as any)?.extension !== "undefined";

const allowed = [".jpg", ".jpeg", ".png", ".bmp", ".gif"];

const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required."),
    communityId: yup
        .string()
        .required("Commnity is required.")
        .test(
            "not-empty",
            "Commnity is required",
            (value: string | undefined) => value !== Constants.emptyGuid,
        ),
    typeId: yup
        .string()
        .required("Banner type is required.")
        .test(
            "not-empty",
            "Banner is required",
            (value: string | undefined) => value !== Constants.emptyGuid,
        ),
    image: yup
        .mixed<ImageInput>()
        .required("Image is required")
        .test({
            name: "file-type",
            message:
                "Please provide a supported file type like .jpg,.jpeg,.png,.bmp,.gif",
            test: (file: ImageInput | undefined, _ctx: TestContext) => {
                if (!file) return true;

                let ext = "";

                if (typeof file === "string") {
                    ext = `.${file.split(".").pop()?.toLowerCase() || ""}`;
                } else if (file instanceof File) {
                    ext = `.${file.name.split(".").pop()?.toLowerCase() || ""}`;
                } else if (hasExtension(file) && file.extension) {
                    ext = file.extension.toLowerCase();
                    if (!ext.startsWith(".")) ext = `.${ext}`;
                }

                return allowed.includes(ext);
            },
        }),
    url: yup.string().optional().notRequired().url("Please enter valid url"),
    startDate: yup
        .date()
        .typeError("Please enter a valid date.")
        .nullable()
        .required("Start date is required."),
    endDate: yup
        .date()
        .typeError("Please enter a valid date.")
        .nullable()
        .test(
            "end-after-start",
            "End date must be after start date",
            function (value) {
                const { startDate } = this.parent;
                if (!value || !startDate) return true;
                const end = new Date(value);
                end.setHours(0, 0, 0, 0);
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                return end.getTime() > start.getTime();
            },
        ),
});

export const BannerForm = (props: BannerFormProps) => {
    const resolvedCommunityId = useResolvedBannerCommunityId(
        props.communityId,
        props.data?.communityId,
    );
    const bannerLocationQuery =
        BannerService.getBannerLocationLookups(resolvedCommunityId);
    const title: string =
        StringHelper.whenNullOrEmpty(props.data?.id, Constants.emptyGuid) ===
            Constants.emptyGuid
            ? "New Banner"
            : "Edit Banner";

    const onSubmit = (data: BannerDto) => {
        if (!data.url) {
            data.url = undefined;
        }
        if (!data.endDate) {
            data.endDate = undefined;
        }
        return Promise.resolve(props.onSubmit({ ...data })).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };

    const isNew = !props.data?.id || props.data?.id === Constants.emptyGuid;
    const isLoading = props.show && !isNew && !props.data;
    const todayDate: string | null | undefined =
        FormatHelper.formatDateForDatePicker(new Date());
    const computedInitialSort = isNew
        ? (props.totalRecords ?? 0) + 1
        : props.data?.sortValue;
    const maxSortValue = !isNew ? props.totalRecords : undefined;

    return (
        <Drawer.Form
            className="w-40"
            show={props.show}
            backdrop={true}
            placement="end"
            scroll={true}
            onHide={props.onHide}
            title={title}
            initialValues={{
                image: undefined,
                ...props.data,
                communityId: resolvedCommunityId,
                sortValue: computedInitialSort,
            }}
            validationSchema={validationSchema}
            on_Submit={onSubmit}
        >
            <Form.PanelSection iconClassName="bi bi-info-circle" title="1. Basic Information">
                <Form.Text
                    name="name"
                    placeholder="Name"
                    label="Name"
                    isLoading={isLoading}
                    required
                />
                <Form.Text
                    name="description"
                    placeholder="Description"
                    label="Description"
                    isLoading={isLoading}
                />
                <Form.Dropdown
                    name={"typeId"}
                    label={"Banner Type"}
                    blankOption={{
                        key: Constants.emptyGuid,
                        display: "--Select Type--",
                        value: Constants.emptyGuid,
                    }}
                    options={LookupDto.mapToOptions(props.bannerTypes)}
                    isLoading={isLoading || props.bannerTypesLoading}
                />
                <Form.Text
                    name="url"
                    placeholder="Url"
                    label="Url"
                    isLoading={isLoading}
                />
            </Form.PanelSection>

            <Form.PanelSection
                iconClassName="bi bi-image"
                title="2. Appearance"
                style={{ marginTop: 16 }}
            >
                <Form.Group
                    label="Image"
                    note="Recommended Size 700 x 300"
                    isLoading={isLoading}
                >
                    <Form.ImageCrop
                        name="image"
                        loading={isLoading}
                        aspect={700 / 300}
                        maxOutputWidth={700}
                        previewMaxHeight={150}
                        modalTitle="Banner Image Crop"
                        chooseText="Choose Banner Image"
                        changeText="Change Image"
                    />
                </Form.Group>
            </Form.PanelSection>

            <Form.PanelSection
                iconClassName="bi bi-calendar-range"
                title="3. Placement"
                style={{ marginTop: 16 }}
            >
                <Form.Autocomplete
                    name="locations"
                    label="Area"
                    id="locations"
                    labelKey="name"
                    placeholder="Select Area"
                    multiple={true}
                    isLoading={isLoading || bannerLocationQuery.isLoading}
                    options={
                        bannerLocationQuery.data?.map((opt: LookupDto) => ({
                            id: opt.id,
                            name: opt.display ?? opt.code ?? opt.id,
                        })) ?? []
                    }
                />
                <Form.Date
                    name="startDate"
                    label="Start Date"
                    isLoading={isLoading}
                    placeholder="Enter date"
                    min={todayDate}
                />
                <Form.Date
                    name="endDate"
                    label="End Date"
                    isLoading={isLoading}
                    placeholder="Enter date"
                    min={todayDate}
                    useStartDateForMin="startDate"
                />
                <Form.Number
                    name="sortValue"
                    label="Display Order"
                    min={1}
                    max={maxSortValue}
                    placeholder="Enter number"
                />
            </Form.PanelSection>
        </Drawer.Form>
    );
};